import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils-app";
import { GoogleLogoIcon as GoogleLogo, DogIcon as Dog, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import TurnstileWidget, { isPreviewHost } from "@/components/TurnstileWidget";
import { consumePostAuthRedirect } from "@/lib/post-auth-redirect";
import { track } from "@/lib/analytics";
import api from "@/lib/api";
import { US_STATES, OUTSIDE_US } from "@/lib/us-states";
import { COUNTRIES } from "@/lib/countries";
import AppleSignInButton from "@/components/AppleSignInButton";
import { isNativeApp } from "@/lib/nativeBridge";
import OptionListPicker from "@/components/OptionListPicker";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  // Captured from the landing-page hero search ("?breed=Cane Corso").
  // Persisted to localStorage so the post-login marketplace can
  // pre-filter results to the breed the visitor was after.
  const breedIntent = (searchParams.get("breed") || "").trim();
  // `?intent=seller` flips the page from buyer-flavoured ("Looking
  // for X breeders") to seller-flavoured ("Be the first verified
  // X breeder") — fired when the landing-page hero search comes back
  // with zero matches in the catalog.
  const isSellerIntent = (searchParams.get("intent") || "") === "seller";
  React.useEffect(() => {
    if (breedIntent) {
      try {
        if (isSellerIntent) {
          // Seller path: persist a separate key the dashboard reads to
          // render the "Welcome — let's register your first X" banner.
          // Cleared on dismiss or on first registered dog.
          localStorage.setItem("signup_seller_intent_breed", breedIntent);
          localStorage.removeItem("signup_breed_intent");
        } else {
          localStorage.setItem("signup_breed_intent", breedIntent);
        }
      } catch {}
    }
  }, [breedIntent, isSellerIntent]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  // US-only signup policy — legal docs + insurance cover US customers
  // only right now. "INTL" switches the card to a waitlist capture.
  const [usState, setUsState] = useState("");
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistCountry, setWaitlistCountry] = useState("");
  const [waitlistDone, setWaitlistDone] = useState(false);
  // Worldwide mode: when the admin lifts the us_only flag, "Outside the
  // United States" registers normally with a country picker instead of
  // showing the waitlist.
  const [policy, setPolicy] = useState(null);
  const [intlCountry, setIntlCountry] = useState("");
  // Two-step location: "United States" → state picker; "Another
  // country" → country picker. International visitors no longer scroll
  // past 50 states to find themselves at the bottom of a US list.
  const [locMode, setLocMode] = useState("");
  const isInternational = usState === OUTSIDE_US;

  // Pre-select location from the browser locale (one less tap for
  // campaign traffic) — the two buttons still let anyone switch.
  useEffect(() => {
    try {
      // Strip POSIX suffixes ("en-US@posix") and parse the region with a
      // tolerant regex first, Intl.Locale as backup.
      const raw = (navigator.language || "").split("@")[0].trim();
      if (!raw) return;
      const m = raw.match(/[-_]([A-Za-z]{2})(?:\b|$)/);
      let region = m ? m[1].toUpperCase() : "";
      if (!region) {
        try { region = (new Intl.Locale(raw).region || "").toUpperCase(); } catch { /* noop */ }
      }
      if (!region) return;
      if (region === "US") {
        setLocMode("US");
      } else if (COUNTRIES.some(([code]) => code === region)) {
        setLocMode("INTL");
        setUsState(OUTSIDE_US);
        setIntlCountry(region);
      }
    } catch { /* locale unavailable — user picks manually */ }
  }, []);
  const intlBlocked = isInternational && policy?.us_only !== false;
  const turnstileRequired = !!process.env.REACT_APP_TURNSTILE_SITE_KEY && !isPreviewHost();

  useEffect(() => {
    api.get("/public/signup-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/international-waitlist", {
        email: waitlistEmail,
        country: waitlistCountry,
      });
      setWaitlistDone(true);
      track("international_waitlist_joined", { origin: "register_page" });
    } catch {
      toast.error("Could not join the waitlist — please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Age gate is an attestation checkbox (the terms checkbox states
  // "I am at least 18") — Apple guideline 5.1.1(v) forbids requiring a
  // date of birth that isn't core to app functionality.

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please accept the Terms and Privacy Policy to continue");
      return;
    }
    if (turnstileRequired && !turnstileToken) {
      toast.error("Please complete the security check below.");
      return;
    }
    if (!usState || isInternational) {
      toast.error("Please select your state");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name, turnstileToken, {
        country: "US",
        us_state: usState,
      });
      toast.success("Account created");
      const dest = consumePostAuthRedirect("/dashboard");
      // Conversion event: fire only when the user came in via a deep-link
      // (typically a marketplace "Sign up to contact breeder" pill). Lets
      // us see signup_cta_clicked → signup_completed_from_listing → inquiry_submitted
      // as a single funnel in PostHog.
      const m = /^\/dogs\/([^/?#]+)/.exec(dest || "");
      if (m) {
        track("signup_completed_from_listing", {
          intent: searchParams.get("intent") || "buyer",
          listing_id: m[1],
        });
      }
      navigate(dest);
    } catch (err) {
      toast.error(formatApiError(err));
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6 font-manrope relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(circle at 85% 15%, rgba(201,162,39,0.12), transparent 55%), radial-gradient(circle at 12% 88%, rgba(193,39,45,0.08), transparent 60%)",
        }}
      />
      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center text-center mb-8">
          <img
            src="/logo-256.png"
            alt="Armstrong K9 Registry"
            className="h-24 w-24 rounded-full object-contain mb-4"
          />
          <h2 className="font-spectral text-2xl tracking-tight text-ink">
            Armstrong K9 Registry
          </h2>
          <p className="mt-2 text-sm text-[#6B6B6B] max-w-xs leading-relaxed">
            The trust-and-safety registry for purebred and exotic dogs.
          </p>
          {breedIntent && (
            isSellerIntent ? (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0E0E0E] text-gold px-3 py-1.5 text-xs font-semibold ring-1 ring-[#C9A227]/40"
                data-testid="breed-intent-badge-seller"
              >
                <Sparkle size={14} weight="fill" />
                Be the first verified {breedIntent} kennel
              </div>
            ) : (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C9A227]/12 text-[#8A6A00] px-3 py-1.5 text-xs font-semibold"
                data-testid="breed-intent-badge"
              >
                <Dog size={14} weight="duotone" />
                Looking for {breedIntent} breeders
              </div>
            )
          )}
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 shadow-[0_8px_30px_rgba(90,107,86,0.06)]">
          <h1 className="font-spectral text-3xl mb-1 tracking-tight">
            {isSellerIntent && breedIntent ? `Claim ${breedIntent}` : "Start your registry"}
          </h1>
          <p className="text-sm text-[#6B6B6B] mb-6">
            {isSellerIntent && breedIntent
              ? `No verified ${breedIntent} kennels yet — yours can be the first.`
              : "Create an account in seconds"}
          </p>
          <div className="mb-4">
            <Label htmlFor="location" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Where are you located?
            </Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setLocMode("US"); setUsState(""); setIntlCountry(""); }}
                className={`h-11 rounded-full border-2 text-[13px] font-semibold transition-colors ${
                  locMode === "US"
                    ? "border-ink bg-ink text-white"
                    : "border-[#E0E0E0] bg-white text-[#3F3F3F] hover:border-ink"
                }`}
                data-testid="register-loc-us"
              >
                United States
              </button>
              <button
                type="button"
                onClick={() => { setLocMode("INTL"); setUsState(OUTSIDE_US); }}
                className={`h-11 rounded-full border-2 text-[13px] font-semibold transition-colors ${
                  locMode === "INTL"
                    ? "border-ink bg-ink text-white"
                    : "border-[#E0E0E0] bg-white text-[#3F3F3F] hover:border-ink"
                }`}
                data-testid="register-loc-intl"
              >
                Another country
              </button>
            </div>
            {locMode === "US" && (
              <div className="mt-2">
                <OptionListPicker
                  options={US_STATES}
                  value={usState}
                  onChange={setUsState}
                  placeholder="Type to find your state…"
                  testId="register-location-select"
                  maxHeight="max-h-44"
                />
              </div>
            )}
          </div>
          {intlBlocked ? (
            <div data-testid="register-international-notice">
              <p className="text-sm text-[#3F3F3F] leading-relaxed bg-gold/5 border border-gold/30 rounded-lg p-3.5">
                Armstrong K9 Registry is currently available to customers in the
                United States only &mdash; our legal agreements and insurance
                coverage are US-specific today. International support is coming
                soon. Leave your email and we&rsquo;ll notify you the moment your
                country is supported.
              </p>
              {waitlistDone ? (
                <p className="mt-4 text-sm font-semibold text-emerald-700" data-testid="register-waitlist-done">
                  You&rsquo;re on the list — we&rsquo;ll be in touch.
                </p>
              ) : (
                <form onSubmit={joinWaitlist} className="mt-4 space-y-3">
                  <Input
                    type="email"
                    required
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="rounded-lg p-3"
                    data-testid="register-waitlist-email"
                  />
                  <Input
                    type="text"
                    required
                    value={waitlistCountry}
                    onChange={(e) => setWaitlistCountry(e.target.value)}
                    placeholder="Your country"
                    className="rounded-lg p-3"
                    data-testid="register-waitlist-country"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-11"
                    data-testid="register-waitlist-submit"
                  >
                    {loading ? "Joining…" : "Notify me at launch"}
                  </Button>
                </form>
              )}
            </div>
          ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {isInternational && (
              <div>
                <Label htmlFor="intl-country" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Country
                </Label>
                <div className="mt-1">
                  <OptionListPicker
                    options={COUNTRIES}
                    value={intlCountry}
                    onChange={setIntlCountry}
                    placeholder="Type to find your country…"
                    testId="register-country-select"
                    maxHeight="max-h-44"
                  />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Your name
              </Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-1 rounded-lg p-3"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-1 rounded-lg p-3"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-1 rounded-lg p-3"
              />
            </div>
            <div className="flex items-start gap-2 pt-1">
              <input
                id="agree-terms"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[#E5E5E5] text-ink focus:ring-[#0E0E0E] cursor-pointer"
                data-testid="register-agree-checkbox"
              />
              <Label htmlFor="agree-terms" className="text-xs text-[#6B6B6B] leading-relaxed cursor-pointer">
                I am at least 18 years old and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-ink underline underline-offset-2" data-testid="register-terms-link">
                  Terms of Service
                </Link>
                {" "}and{" "}
                <Link to="/privacy" target="_blank" className="text-ink underline underline-offset-2" data-testid="register-privacy-link">
                  Privacy Policy
                </Link>
                .
              </Label>
            </div>
            <TurnstileWidget onVerify={setTurnstileToken} testId="register-turnstile" />
            <Button
              type="submit"
              disabled={loading || !agreed || !usState || (isInternational && !intlCountry) || (turnstileRequired && !turnstileToken)}
              className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-11 disabled:opacity-60"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          )}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#E5E5E5]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#6B6B6B]">or</span>
            <div className="flex-1 h-px bg-[#E5E5E5]" />
          </div>
          <Button
            type="button"
            onClick={startGoogleLogin}
            variant="outline"
            className={`w-full rounded-full h-11 border-[#E5E5E5] hover:bg-[#FAFAFA] ${isNativeApp() ? "hidden" : ""}`}
            data-testid="register-google-btn"
          >
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>
          <div className="mt-2">
            <AppleSignInButton mode="register" />
          </div>
          <p className="text-[11px] text-center text-[#6B6B6B] mt-3 leading-relaxed" data-testid="social-clickwrap-note">
            By continuing with Google or Apple, you confirm you are at least 18
            and agree to the{" "}
            <Link to="/terms" target="_blank" className="underline underline-offset-2">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="underline underline-offset-2">Privacy Policy</Link>.
          </p>
          <p className="text-sm text-center text-[#6B6B6B] mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-ink font-semibold" data-testid="go-login-link">
              Sign in
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-6 text-[11px] text-[#6B6B6B]">
          <Link to="/terms" className="hover:text-ink">Terms</Link>
          <Link to="/privacy" className="hover:text-ink">Privacy</Link>
          <Link to="/consent" className="hover:text-ink">User Consent</Link>
          <Link to="/contact" className="hover:text-ink">Contact</Link>
        </div>
      </div>
    </div>
  );
}
